"use client";

import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useFetchNewsArticles } from "@/apis/queryFunctions/news";
import useTrans from "@/hooks/useTrans";
import useWindowSize from "@/hooks/useWindowSize";
import appendImageUrlFromAPI from "@/utils/appendImageUrlFromAPI";
import { useTranslatedText } from "@/hooks/useProductTranslation";
import ActionButton from "@/components/Common/ActionButton";
import { Title, Description } from "@/components/Common/Typography";
import styles from "./newsSection.module.css";

export interface NewsSectionProps {
  isActive?: boolean;
  locale: string;
  title?: string;
  subtitle?: string;
  decoImage?: string;
}

function NewsCardTitle({ title, locale, className, style }: { title: string; locale: string; className?: string; style?: React.CSSProperties }) {
  const displayTitle = useTranslatedText(title, locale);
  return (
    <h3 className={className} style={style}>
      {displayTitle}
    </h3>
  );
}

function SmallNewsCardTitle({ title, locale, className, style }: { title: string; locale: string; className?: string; style?: React.CSSProperties }) {
  const displayTitle = useTranslatedText(title, locale);
  return (
    <h4 className={className} style={style}>
      {displayTitle}
    </h4>
  );
}

export default function NewsSection({
  isActive,
  locale,
  title = locale === "vi" ? "TIN TỨC MỚI NHẤT" : "LATEST NEWS",
  subtitle = locale === "vi"
    ? "Cập nhật thông tin nổi bật mới nhất"
    : "Stay updated with the latest news",
  decoImage,
}: NewsSectionProps) {
  const trans = useTrans();

  const { data: news } = useFetchNewsArticles({
    condition: {
      ngon_ngu: locale,
      loai_tin_tuc: {
        $in: ["Tin tức", "Tin tức Việt Thiên", "Tin tức cà phê"],
      },
    },
  });

  const { isMobile, isTablet, isDesktop } = useWindowSize();
  const latest = news?.[0];
  const rightList = news ? news.slice(1, 4) : [];

  const sizePack = isMobile
    ? {
        bigTitle: "18px",
        bigImgHeight: 400,
        sectionPadding: "24px 0 20px 0",
      }
    : isTablet
      ? {
          bigTitle: "22px",
          bigImgHeight: 500,
          sectionPadding: "80px 0 50px 0",
        }
      : {
          bigTitle: "20px",
          bigImgHeight: 320,
          sectionPadding: "70px 0 30px 0",
        };

  return (
    <section
      className={styles.section}
      style={{ padding: sizePack.sectionPadding }}
    >
      <div className={`${styles.inner} ${isActive ? styles.inView : ""}`}>
        <div className={styles.container}>
          <div className={styles.grid}>
            {/* LEFT COLUMN: BIG NEWS */}
            <div className={styles.leftCol}>
              <div className={styles.header}>
                <Title size="medium" style={{ marginBottom: "6px" }}>
                  {title}
                </Title>
                {subtitle && (
                  <Description size="medium" className={styles.subtitle}>
                    {subtitle}
                  </Description>
                )}
              </div>

              {latest ? (
                <a href={`/${locale}/tin-tuc/${latest.slug}`} className={styles.bigCard}>
                  <div
                    style={{
                      height: sizePack.bigImgHeight,
                      position: "relative",
                    }}
                  >
                    <Image
                      src={appendImageUrlFromAPI({
                        src: latest.picture,
                        size: "l",
                      })}
                      alt={latest.title}
                      fill
                      style={{ objectFit: "cover" }}
                    />
                    <div className={styles.bigOverlay} />
                    <div className={styles.bigInfo}>
                      <NewsCardTitle
                        title={latest.title}
                        locale={locale}
                        className={styles.bigNewsTitle}
                        style={{ fontSize: sizePack.bigTitle }}
                      />
                      <p className={styles.date}>
                        {new Date(latest.date_updated).toLocaleDateString(
                          locale
                        )}
                      </p>
                    </div>
                  </div>
                </a>
              ) : (
                <div
                  style={{
                    height: sizePack.bigImgHeight,
                    backgroundColor: "#f2f2f2",
                    borderRadius: 16,
                  }}
                />
              )}
            </div>

            {/* RIGHT COLUMN: SMALL LIST */}
            <div className={styles.rightCol}>
              <div className={styles.smallList}>
                {!isMobile && (
                  <img
                    className={styles.decoImg}
                    src={
                      decoImage
                        ? appendImageUrlFromAPI({ src: decoImage, size: "2k" })
                        : "/images/Layer_1.png"
                    }
                    alt="deco"
                  />
                )}

                {rightList.map((item) => (
                  <a
                    key={item._id}
                    href={`/${locale}/tin-tuc/${item.slug}`}
                    className={styles.smallCard}
                  >
                    <div className={styles.smallImgWrapper}>
                      <Image
                        src={appendImageUrlFromAPI({
                          src: item.picture,
                          size: "s",
                        })}
                        alt={item.title}
                        width={400}
                        height={300}
                        style={{ objectFit: "cover" }}
                      />
                    </div>
                    <div className={styles.smallContent}>
                      <SmallNewsCardTitle
                        title={item.title}
                        locale={locale}
                        className={styles.smallNewsTitle}
                        style={{ fontSize: "14px" }}
                      />
                      <p className={styles.smallDate}>
                        {new Date(item.date_updated).toLocaleDateString(locale)}
                      </p>
                    </div>
                  </a>
                ))}

                <div className={styles.btnWrapper}>
                  <ActionButton
                    label={locale === "vi" ? "Xem tất cả tin tức" : "View all news"}
                    link={`/${locale}/tin-tuc`}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
